import React, { useState, useEffect } from 'react';
import { 
  Mail, Bell, Plug, HardDrive, Palette, Shield, 
  Map, DollarSign, GitBranch, FileText, Tag, Settings,
  Save, Loader2, CheckCircle2, AlertCircle, Webhook, MessageSquare, Handshake, MessageCircle, UserPlus, Database, Activity
} from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../hooks/use-toast';
import AiConfigPanel from '../../components/admin/AiConfigPanel';
import WebhooksIntegrations from '../../components/WebhooksIntegrations';
import NotificationTemplates from '../../components/NotificationTemplates';
import NegotiationConfigPanel from '../../components/NegotiationConfigPanel';
import WhatsAppConfigPanel from '../../components/WhatsAppConfigPanel';
import OpenWaConfigPanel from '../../components/OpenWaConfigPanel';
import RegistrationConfigPanel from '../../components/RegistrationConfigPanel';
import TruckDatabaseConfigPanel from '../../components/TruckDatabaseConfigPanel';
import TruckDBMonitoringDashboard from '../../components/TruckDBMonitoringDashboard';
import { MultiTokenInput, validateEmail, normalizeEmail } from '../../components/common/MultiTokenInput';

const SystemConfiguration = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [activeTab, setActiveTab] = useState('email');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testingEmail, setTestingEmail] = useState(false);
  const [config, setConfig] = useState(null);

  const tabs = [
    { id: 'registration', label: 'Inscriptions', icon: UserPlus, color: 'text-emerald-600' },
    { id: 'truck-database', label: 'Truck Database', icon: Database, color: 'text-blue-600' },
    { id: 'truck-monitoring', label: 'Monitoring Sync', icon: Activity, color: 'text-cyan-600' },
    { id: 'webhooks', label: 'Webhooks & Automatisation', icon: Webhook, color: 'text-purple-600' },
    { id: 'email', label: 'Email & Templates', icon: Mail, color: 'text-blue-600' },
    { id: 'whatsapp', label: 'WhatsApp (archive Meta)', icon: MessageCircle, color: 'text-green-600' },
    { id: 'openwa', label: 'WhatsApp OpenWA', icon: MessageCircle, color: 'text-emerald-600' },
    { id: 'notifications', label: 'Notifications', icon: Bell, color: 'text-yellow-600' },
    { id: 'notification-templates', label: 'Templates Notifications', icon: MessageSquare, color: 'text-indigo-600' },
    { id: 'negotiation', label: 'Négociation', icon: Handshake, color: 'text-amber-600' },
    { id: 'api', label: 'APIs & Intégrations', icon: Plug, color: 'text-purple-600' },
    { id: 'ai', label: 'Intelligence Artificielle', icon: Activity, color: 'text-indigo-600' },
    { id: 'storage', label: 'Stockage', icon: HardDrive, color: 'text-green-600' },
    { id: 'branding', label: 'Branding', icon: Palette, color: 'text-pink-600' },
    { id: 'security', label: 'Sécurité', icon: Shield, color: 'text-red-600' },
    { id: 'maps', label: 'Cartes', icon: Map, color: 'text-teal-600' },
    { id: 'currency', label: 'Devises', icon: DollarSign, color: 'text-emerald-600' },
    { id: 'workflow', label: 'Workflow', icon: GitBranch, color: 'text-indigo-600' },
    { id: 'documents', label: 'Documents', icon: FileText, color: 'text-orange-600' },
    { id: 'pricing', label: 'Tarification', icon: Tag, color: 'text-cyan-600' },
    { id: 'maintenance', label: 'Maintenance', icon: Settings, color: 'text-gray-600' }
  ];

  useEffect(() => {
    fetchConfiguration();
  }, []);

  const fetchConfiguration = async () => {
    setLoading(true);
    try {
      // SEC-Fix : axios auto-injecte le cookie HttpOnly + CSRF (cf AuthContext interceptor)
      const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/system-config/`);
      setConfig(response.data);
    } catch (error) {
      toast({
        title: 'Erreur',
        description: error.response?.data?.detail || error.message || 'Erreur lors du chargement de la configuration',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const saveConfiguration = async (section, data) => {
    setSaving(true);
    try {
      // SEC-Fix : axios auto-injecte X-CSRF-Token sur PUT/POST/PATCH/DELETE
      const response = await axios.put(`${process.env.REACT_APP_BACKEND_URL}/api/system-config/`, {
        section,
        data
      });
      setConfig(response.data);

      toast({
        title: 'Succès',
        description: 'Configuration enregistrée avec succès',
        variant: 'default'
      });
    } catch (error) {
      toast({
        title: 'Erreur',
        description: error.response?.data?.detail || error.message || 'Erreur lors de la sauvegarde',
        variant: 'destructive'
      });
    } finally {
      setSaving(false);
    }
  };

  const testEmailConfiguration = async () => {
    setTestingEmail(true);
    try {
      // Use test_email if configured, otherwise use current user's email
      const testEmail = config.email.test_email || user?.email;
      
      if (!testEmail) {
        throw new Error('Veuillez configurer un email de test ou vous connecter');
      }

      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/system-config/test-email`,
        { test_email: testEmail }
      );
      const data = response.data;

      toast({
        title: 'Test réussi',
        description: data?.message || `Email de test envoyé à ${testEmail}`,
        variant: 'default'
      });
    } catch (error) {
      toast({
        title: 'Échec du test',
        description: error.response?.data?.detail || error.message || 'Erreur lors du test',
        variant: 'destructive'
      });
    } finally {
      setTestingEmail(false);
    }
  };

  const renderEmailConfig = () => {
    if (!config?.email) return null;

    return (
      <div className="space-y-6">
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-blue-600 mt-0.5" />
          <div>
            <h4 className="font-semibold text-blue-900">Configuration Email</h4>
            <p className="text-sm text-blue-700 mt-1">
              Configurez votre service d'envoi d'emails pour activer les notifications et communications automatiques.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Fournisseur Email
            </label>
            <select
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              value={config.email.provider}
              onChange={(e) => setConfig({
                ...config,
                email: { ...config.email, provider: e.target.value }
              })}
            >
              <option value="sendgrid">SendGrid</option>
              <option value="smtp">SMTP</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Statut
            </label>
            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                className="w-5 h-5 text-blue-600"
                checked={config.email.enabled}
                onChange={(e) => setConfig({
                  ...config,
                  email: { ...config.email, enabled: e.target.checked }
                })}
              />
              <span className="text-sm text-gray-700">
                {config.email.enabled ? 'Activé' : 'Désactivé'}
              </span>
            </div>
          </div>
        </div>

        {config.email.provider === 'sendgrid' && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Clé API SendGrid *
            </label>
            <input
              type="password"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              placeholder="SG.xxxxxxxxxxxxxxxxxx"
              value={config.email.sendgrid_api_key || ''}
              onChange={(e) => setConfig({
                ...config,
                email: { ...config.email, sendgrid_api_key: e.target.value }
              })}
            />
            <p className="text-xs text-gray-500 mt-1">
              Obtenez votre clé API sur{' '}
              <a href="https://app.sendgrid.com/settings/api_keys" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                SendGrid Dashboard
              </a>
            </p>
          </div>
        )}

        {config.email.provider === 'smtp' && (
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Hôte SMTP *
                </label>
                <input
                  type="text"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="smtp.example.com"
                  value={config.email.smtp_host || ''}
                  onChange={(e) => setConfig({
                    ...config,
                    email: { ...config.email, smtp_host: e.target.value }
                  })}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Port SMTP *
                </label>
                <input
                  type="number"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="587"
                  value={config.email.smtp_port || ''}
                  onChange={(e) => setConfig({
                    ...config,
                    email: { ...config.email, smtp_port: parseInt(e.target.value) }
                  })}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Nom d'utilisateur SMTP *
                </label>
                <input
                  type="text"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  value={config.email.smtp_username || ''}
                  onChange={(e) => setConfig({
                    ...config,
                    email: { ...config.email, smtp_username: e.target.value }
                  })}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Mot de passe SMTP *
                </label>
                <input
                  type="password"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  value={config.email.smtp_password || ''}
                  onChange={(e) => setConfig({
                    ...config,
                    email: { ...config.email, smtp_password: e.target.value }
                  })}
                />
              </div>
            </div>
            
            {/* Options de sécurité SMTP */}
            <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-sm font-medium text-blue-800 mb-3">Configuration de la sécurité SMTP</p>
              <p className="text-xs text-blue-600 mb-3">
                • <strong>SSL direct</strong> : Connexion chiffrée dès le départ (souvent port 465)<br/>
                • <strong>STARTTLS</strong> : Connexion non chiffrée puis upgrade vers TLS (souvent port 587)<br/>
                • Ces options sont indépendantes du port - configurez selon votre serveur SMTP
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="smtp_use_ssl"
                    className="w-4 h-4 text-blue-600 rounded"
                    checked={config.email.smtp_use_ssl || false}
                    onChange={(e) => {
                      const useSSL = e.target.checked;
                      setConfig({
                        ...config,
                        email: { 
                          ...config.email, 
                          smtp_use_ssl: useSSL,
                          // Si SSL activé, désactiver STARTTLS (mutuellement exclusifs)
                          smtp_use_starttls: useSSL ? false : config.email.smtp_use_starttls
                        }
                      });
                    }}
                  />
                  <label htmlFor="smtp_use_ssl" className="text-sm text-gray-700">
                    SSL direct
                  </label>
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="smtp_use_starttls"
                    className="w-4 h-4 text-blue-600 rounded"
                    checked={config.email.smtp_use_starttls !== false}
                    onChange={(e) => {
                      const useStartTLS = e.target.checked;
                      setConfig({
                        ...config,
                        email: { 
                          ...config.email, 
                          smtp_use_starttls: useStartTLS,
                          // Si STARTTLS activé, désactiver SSL (mutuellement exclusifs)
                          smtp_use_ssl: useStartTLS ? false : config.email.smtp_use_ssl
                        }
                      });
                    }}
                  />
                  <label htmlFor="smtp_use_starttls" className="text-sm text-gray-700">
                    STARTTLS
                  </label>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Email expéditeur *
            </label>
            <input
              type="email"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              placeholder="contact@example.com"
              value={config.email.from_email || ''}
              onChange={(e) => setConfig({
                ...config,
                email: { ...config.email, from_email: e.target.value }
              })}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Nom expéditeur *
            </label>
            <input
              type="text"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              placeholder="Baobab Connect"
              value={config.email.from_name || ''}
              onChange={(e) => setConfig({
                ...config,
                email: { ...config.email, from_name: e.target.value }
              })}
            />
          </div>
        </div>

        {/* Email de test */}
        <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Email de test (pour les tests de connexion)
          </label>
          <div className="flex gap-2">
            <input
              type="email"
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              placeholder={user?.email || 'test@example.com'}
              value={config.email.test_email || ''}
              onChange={(e) => setConfig({
                ...config,
                email: { ...config.email, test_email: e.target.value }
              })}
            />
          </div>
          <p className="text-xs text-gray-500 mt-1">
            Si vide, l'email de test sera envoyé à votre adresse ({user?.email || 'votre email'})
          </p>
        </div>

        <div className="flex gap-4 pt-4">
          <button
            onClick={() => saveConfiguration('email', config.email)}
            disabled={saving}
            className="flex items-center gap-2 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
          >
            {saving ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <Save className="w-5 h-5" />
            )}
            Enregistrer
          </button>
          <button
            onClick={testEmailConfiguration}
            disabled={testingEmail || !config.email.enabled}
            className="flex items-center gap-2 px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
          >
            {testingEmail ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <CheckCircle2 className="w-5 h-5" />
            )}
            Tester la connexion
          </button>
        </div>
      </div>
    );
  };

  const renderNotificationsConfig = () => {
    if (!config?.notifications) return null;

    return (
      <div className="space-y-6">
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 flex items-start gap-3">
          <Bell className="w-5 h-5 text-yellow-600 mt-0.5" />
          <div>
            <h4 className="font-semibold text-yellow-900">Paramètres de Notifications</h4>
            <p className="text-sm text-yellow-700 mt-1">
              Configurez les canaux et la fréquence des notifications système.
            </p>
          </div>
        </div>

        <div className="space-y-4">
          <div className="flex items-center justify-between p-4 bg-white border rounded-lg">
            <div>
              <h4 className="font-medium text-gray-900">Notifications Email</h4>
              <p className="text-sm text-gray-500">Envoyer des notifications par email</p>
            </div>
            <input
              type="checkbox"
              className="w-5 h-5 text-blue-600"
              checked={config.notifications.email_notifications}
              onChange={(e) => setConfig({
                ...config,
                notifications: { ...config.notifications, email_notifications: e.target.checked }
              })}
            />
          </div>

          <div className="flex items-center justify-between p-4 bg-white border rounded-lg">
            <div>
              <h4 className="font-medium text-gray-900">Notifications In-App</h4>
              <p className="text-sm text-gray-500">Afficher les notifications dans l'application</p>
            </div>
            <input
              type="checkbox"
              className="w-5 h-5 text-blue-600"
              checked={config.notifications.in_app_notifications}
              onChange={(e) => setConfig({
                ...config,
                notifications: { ...config.notifications, in_app_notifications: e.target.checked }
              })}
            />
          </div>

          <div className="flex items-center justify-between p-4 bg-white border rounded-lg">
            <div>
              <h4 className="font-medium text-gray-900">Notifications SMS</h4>
              <p className="text-sm text-gray-500">Envoyer des SMS (nécessite configuration)</p>
            </div>
            <input
              type="checkbox"
              className="w-5 h-5 text-blue-600"
              checked={config.notifications.sms_notifications}
              onChange={(e) => setConfig({
                ...config,
                notifications: { ...config.notifications, sms_notifications: e.target.checked }
              })}
            />
          </div>

          <div className="flex items-center justify-between p-4 bg-white border rounded-lg">
            <div>
              <h4 className="font-medium text-gray-900">Notifications Push</h4>
              <p className="text-sm text-gray-500">Notifications push web (à venir)</p>
            </div>
            <input
              type="checkbox"
              className="w-5 h-5 text-blue-600"
              checked={config.notifications.push_notifications}
              onChange={(e) => setConfig({
                ...config,
                notifications: { ...config.notifications, push_notifications: e.target.checked }
              })}
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Fréquence des notifications
          </label>
          <select
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500"
            value={config.notifications.notification_frequency}
            onChange={(e) => setConfig({
              ...config,
              notifications: { ...config.notifications, notification_frequency: e.target.value }
            })}
          >
            <option value="instant">Instantané</option>
            <option value="hourly">Toutes les heures</option>
            <option value="daily">Quotidien</option>
          </select>
        </div>

        {/* Broadcast email interne (PM Spec 07/05/2026) */}
        <div className="border-t pt-4 mt-4">
          <h4 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
            <Bell className="w-4 h-4 text-emerald-600" />
            Email groupé interne
          </h4>
          <p className="text-xs text-gray-500 mb-3">
            Liste d'adresses qui recevront <span className="font-medium">une copie unique</span> des notifications déclenchées par les actions client (devis accepté, demande de modification, validation/litige POD…). Évite les emails individuels en doublon.
          </p>

          <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg mb-3">
            <span className="text-sm font-medium">Activer le broadcast interne</span>
            <input
              type="checkbox"
              className="w-5 h-5 text-yellow-600"
              checked={config.notifications.internal_broadcast_enabled !== false}
              onChange={(e) => setConfig({
                ...config,
                notifications: { ...config.notifications, internal_broadcast_enabled: e.target.checked }
              })}
              data-testid="broadcast-enabled-toggle"
            />
          </div>

          <label className="block text-sm font-medium text-gray-700 mb-1">
            Adresses email
          </label>
          <MultiTokenInput
            value={config.notifications.internal_broadcast_emails || []}
            onChange={(emails) => setConfig({
              ...config,
              notifications: { ...config.notifications, internal_broadcast_emails: emails }
            })}
            placeholder="ops@baobab.ci, manager@baobab.ci"
            validate={validateEmail}
            normalize={normalizeEmail}
            testId="broadcast-emails"
          />
          {(config.notifications.internal_broadcast_emails || []).length > 0 && (
            <p className="text-xs text-emerald-700 mt-2">
              ✓ {config.notifications.internal_broadcast_emails.length} adresse{config.notifications.internal_broadcast_emails.length > 1 ? 's' : ''} configurée{config.notifications.internal_broadcast_emails.length > 1 ? 's' : ''}
            </p>
          )}
        </div>

        <div className="flex gap-4 pt-4">
          <button
            onClick={() => saveConfiguration('notifications', config.notifications)}
            disabled={saving}
            className="flex items-center gap-2 px-6 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 disabled:opacity-50"
          >
            {saving ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <Save className="w-5 h-5" />
            )}
            Enregistrer
          </button>
        </div>
      </div>
    );
  };

  const renderAPIConfig = () => {
    if (!config?.api) return null;

    return (
      <div className="space-y-6">
        <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 flex items-start gap-3">
          <Plug className="w-5 h-5 text-purple-600 mt-0.5" />
          <div>
            <h4 className="font-semibold text-purple-900">APIs & Intégrations</h4>
            <p className="text-sm text-purple-700 mt-1">
              Configurez les services tiers et intégrations externes.
            </p>
          </div>
        </div>

        <div className="space-y-6">
          <div className="border rounded-lg p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Google OAuth 2.0</h3>
            
            <div className="flex items-center gap-3 mb-4">
              <input
                type="checkbox"
                className="w-5 h-5 text-purple-600"
                checked={config.api.google_oauth_enabled}
                onChange={(e) => setConfig({
                  ...config,
                  api: { ...config.api, google_oauth_enabled: e.target.checked }
                })}
              />
              <span className="text-sm font-medium text-gray-700">
                Activer la connexion Google
              </span>
            </div>

            {config.api.google_oauth_enabled && (
              <div className="space-y-4 mt-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Client ID *
                  </label>
                  <input
                    type="text"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                    placeholder="xxxxx.apps.googleusercontent.com"
                    value={config.api.google_client_id || ''}
                    onChange={(e) => setConfig({
                      ...config,
                      api: { ...config.api, google_client_id: e.target.value }
                    })}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Client Secret *
                  </label>
                  <input
                    type="password"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                    placeholder="GOCSPX-xxxxx"
                    value={config.api.google_client_secret || ''}
                    onChange={(e) => setConfig({
                      ...config,
                      api: { ...config.api, google_client_secret: e.target.value }
                    })}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Redirect URI *
                  </label>
                  <input
                    type="text"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                    placeholder="https://votre-domaine.com/auth/google/callback"
                    value={config.api.google_redirect_uri || ''}
                    onChange={(e) => setConfig({
                      ...config,
                      api: { ...config.api, google_redirect_uri: e.target.value }
                    })}
                  />
                </div>
                <p className="text-xs text-gray-500">
                  Obtenez vos identifiants sur{' '}
                  <a href="https://console.cloud.google.com/apis/credentials" target="_blank" rel="noopener noreferrer" className="text-purple-600 hover:underline">
                    Google Cloud Console
                  </a>
                </p>
              </div>
            )}
          </div>

          <div className="border rounded-lg p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Fournisseur de Cartes</h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Service de cartographie
                </label>
                <select
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                  value={config.api.maps_provider}
                  onChange={(e) => setConfig({
                    ...config,
                    api: { ...config.api, maps_provider: e.target.value }
                  })}
                >
                  <option value="openstreetmap">OpenStreetMap (Gratuit)</option>
                  <option value="google">Google Maps</option>
                </select>
              </div>

              {config.api.maps_provider === 'google' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Clé API Google Maps *
                  </label>
                  <input
                    type="password"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                    placeholder="AIzaSyxxxxx"
                    value={config.api.google_maps_api_key || ''}
                    onChange={(e) => setConfig({
                      ...config,
                      api: { ...config.api, google_maps_api_key: e.target.value }
                    })}
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Obtenez votre clé sur{' '}
                    <a href="https://console.cloud.google.com/google/maps-apis" target="_blank" rel="noopener noreferrer" className="text-purple-600 hover:underline">
                      Google Maps Platform
                    </a>
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="flex gap-4 pt-4">
          <button
            onClick={() => saveConfiguration('api', config.api)}
            disabled={saving}
            className="flex items-center gap-2 px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50"
          >
            {saving ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <Save className="w-5 h-5" />
            )}
            Enregistrer
          </button>
        </div>
      </div>
    );
  };

  const renderStorageConfig = () => {
    if (!config?.storage) return null;
    return (
      <div className="space-y-6">
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-start gap-3">
          <HardDrive className="w-5 h-5 text-green-600 mt-0.5" />
          <div>
            <h4 className="font-semibold text-green-900">Configuration du Stockage</h4>
            <p className="text-sm text-green-700 mt-1">Gérez les fichiers et documents de la plateforme.</p>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Fournisseur de stockage</label>
            <select className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
              value={config.storage.storage_provider}
              onChange={(e) => setConfig({...config, storage: {...config.storage, storage_provider: e.target.value}})}>
              <option value="local">Stockage Local</option>
              <option value="s3">Amazon S3</option>
              <option value="azure">Azure Blob</option>
              <option value="gcp">Google Cloud Storage</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Taille max fichier (MB)</label>
            <input type="number" className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
              value={config.storage.max_file_size_mb}
              onChange={(e) => setConfig({...config, storage: {...config.storage, max_file_size_mb: parseInt(e.target.value)}})} />
          </div>
        </div>
        <div className="flex gap-4 pt-4">
          <button onClick={() => saveConfiguration('storage', config.storage)} disabled={saving}
            className="flex items-center gap-2 px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50">
            {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : <Save className="w-5 h-5" />}
            Enregistrer
          </button>
        </div>
      </div>
    );
  };

  const renderBrandingConfig = () => {
    if (!config?.branding) return null;
    
    const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
    
    const handleLogoUpload = async (e) => {
      const file = e.target.files?.[0];
      if (!file) return;
      
      // Vérifier la taille du fichier (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        toast({ title: 'Erreur', description: 'Le fichier est trop volumineux (max 5MB)', variant: 'destructive' });
        return;
      }
      
      const formData = new FormData();
      formData.append('file', file);
      formData.append('type', 'logo');
      
      try {
        toast({ title: 'Upload en cours...', description: 'Veuillez patienter' });
        
        const response = await axios.post(
          `${BACKEND_URL}/api/upload/branding`,
          formData
        );
        const data = response.data;
        const newConfig = {...config, branding: {...config.branding, logo_url: data.url}};
        setConfig(newConfig);
        
        // Sauvegarder automatiquement
        await saveConfiguration('branding', newConfig.branding);
        toast({ title: 'Succès', description: 'Logo uploadé et sauvegardé avec succès' });
      } catch (error) {
        console.error('Logo upload error:', error);
        toast({ title: 'Erreur', description: error.response?.data?.detail || error.message || 'Erreur lors de l\'upload du logo', variant: 'destructive' });
      }
    };
    
    const handleBackgroundUpload = async (e) => {
      const file = e.target.files?.[0];
      if (!file) return;
      
      // Vérifier la taille du fichier (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        toast({ title: 'Erreur', description: 'Le fichier est trop volumineux (max 5MB)', variant: 'destructive' });
        return;
      }
      
      const formData = new FormData();
      formData.append('file', file);
      formData.append('type', 'background');
      
      try {
        toast({ title: 'Upload en cours...', description: 'Veuillez patienter' });
        
        const response = await axios.post(
          `${BACKEND_URL}/api/upload/branding`,
          formData
        );
        const data = response.data;
        const newConfig = {...config, branding: {...config.branding, login_background_url: data.url, login_background_type: 'image'}};
        setConfig(newConfig);
        
        // Sauvegarder automatiquement
        await saveConfiguration('branding', newConfig.branding);
        toast({ title: 'Succès', description: 'Image de fond uploadée et sauvegardée avec succès' });
      } catch (error) {
        console.error('Background upload error:', error);
        toast({ title: 'Erreur', description: error.response?.data?.detail || error.message || 'Erreur lors de l\'upload de l\'image', variant: 'destructive' });
      }
    };
    
    const handleRemoveLogo = async () => {
      const newConfig = {...config, branding: {...config.branding, logo_url: null}};
      setConfig(newConfig);
      await saveConfiguration('branding', newConfig.branding);
      toast({ title: 'Succès', description: 'Logo supprimé' });
    };
    
    const handleRemoveBackground = async () => {
      const newConfig = {...config, branding: {...config.branding, login_background_url: null}};
      setConfig(newConfig);
      await saveConfiguration('branding', newConfig.branding);
      toast({ title: 'Succès', description: 'Image de fond supprimée' });
    };
    
    return (
      <div className="space-y-6">
        <div className="bg-pink-50 border border-pink-200 rounded-lg p-4 flex items-start gap-3">
          <Palette className="w-5 h-5 text-pink-600 mt-0.5" />
          <div>
            <h4 className="font-semibold text-pink-900">Personnalisation de la Marque</h4>
            <p className="text-sm text-pink-700 mt-1">Personnalisez l&apos;apparence de votre plateforme et de la page de connexion.</p>
          </div>
        </div>
        
        {/* Logo Section */}
        <div className="border rounded-lg p-4 space-y-4">
          <h4 className="font-medium text-gray-900 flex items-center gap-2">
            <svg className="w-5 h-5 text-pink-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            Logo de l&apos;entreprise
          </h4>
          
          <div className="flex items-start gap-6">
            <div className="flex-shrink-0">
              {config.branding.logo_url ? (
                <div className="relative">
                  <img 
                    src={config.branding.logo_url.startsWith('http') ? config.branding.logo_url : `${BACKEND_URL}${config.branding.logo_url}`} 
                    alt="Logo" 
                    className="w-24 h-24 object-contain border rounded-lg bg-white p-2"
                  />
                  <button 
                    onClick={handleRemoveLogo}
                    className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              ) : (
                <div className="w-24 h-24 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center bg-gray-50">
                  <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                </div>
              )}
            </div>
            
            <div className="flex-1 space-y-3">
              <p className="text-sm text-gray-600">
                Ce logo sera affiché sur la page de connexion et les documents (devis, factures).
              </p>
              <div>
                <label className="inline-flex items-center gap-2 px-4 py-2 bg-pink-100 text-pink-700 rounded-lg cursor-pointer hover:bg-pink-200">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                  </svg>
                  Choisir un logo
                  <input type="file" accept="image/*" onChange={handleLogoUpload} className="hidden" />
                </label>
              </div>
              <p className="text-xs text-gray-500">Formats: PNG, JPG, SVG. Taille max: 2MB</p>
            </div>
          </div>
        </div>
        
        {/* Background Section */}
        <div className="border rounded-lg p-4 space-y-4">
          <h4 className="font-medium text-gray-900 flex items-center gap-2">
            <svg className="w-5 h-5 text-pink-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
            Fond de la page de connexion
          </h4>
          
          <div className="flex gap-4 mb-4">
            <label className={`flex-1 p-4 border-2 rounded-lg cursor-pointer transition-all ${
              config.branding.login_background_type === 'gradient' ? 'border-pink-500 bg-pink-50' : 'border-gray-200 hover:border-pink-300'
            }`}>
              <input 
                type="radio" 
                name="bg_type" 
                value="gradient"
                checked={config.branding.login_background_type === 'gradient'}
                onChange={async () => {
                  const newConfig = {...config, branding: {...config.branding, login_background_type: 'gradient'}};
                  setConfig(newConfig);
                  await saveConfiguration('branding', newConfig.branding);
                }}
                className="sr-only"
              />
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-[#1db584] to-[#128f69]"></div>
                <div>
                  <p className="font-medium text-gray-900">Dégradé de couleur</p>
                  <p className="text-sm text-gray-500">Utiliser la couleur primaire</p>
                </div>
              </div>
            </label>
            
            <label className={`flex-1 p-4 border-2 rounded-lg cursor-pointer transition-all ${
              config.branding.login_background_type === 'image' ? 'border-pink-500 bg-pink-50' : 'border-gray-200 hover:border-pink-300'
            }`}>
              <input 
                type="radio" 
                name="bg_type" 
                value="image"
                checked={config.branding.login_background_type === 'image'}
                onChange={async () => {
                  const newConfig = {...config, branding: {...config.branding, login_background_type: 'image'}};
                  setConfig(newConfig);
                  await saveConfiguration('branding', newConfig.branding);
                }}
                className="sr-only"
              />
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-lg bg-gray-200 flex items-center justify-center">
                  <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                </div>
                <div>
                  <p className="font-medium text-gray-900">Image personnalisée</p>
                  <p className="text-sm text-gray-500">Uploader une image de fond</p>
                </div>
              </div>
            </label>
          </div>
          
          {config.branding.login_background_type === 'image' && (
            <div className="flex items-start gap-6 p-4 bg-gray-50 rounded-lg">
              <div className="flex-shrink-0">
                {config.branding.login_background_url ? (
                  <div className="relative">
                    <img 
                      src={config.branding.login_background_url.startsWith('http') ? config.branding.login_background_url : `${BACKEND_URL}${config.branding.login_background_url}`} 
                      alt="Background" 
                      className="w-40 h-24 object-cover border rounded-lg"
                    />
                    <button 
                      onClick={handleRemoveBackground}
                      className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                ) : (
                  <div className="w-40 h-24 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center bg-white">
                    <span className="text-gray-400 text-sm">Pas d&apos;image</span>
                  </div>
                )}
              </div>
              
              <div className="flex-1 space-y-3">
                <p className="text-sm text-gray-600">
                  Cette image sera affichée en fond sur la page de connexion.
                </p>
                <div>
                  <label className="inline-flex items-center gap-2 px-4 py-2 bg-pink-100 text-pink-700 rounded-lg cursor-pointer hover:bg-pink-200">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                    </svg>
                    Choisir une image
                    <input type="file" accept="image/*" onChange={handleBackgroundUpload} className="hidden" />
                  </label>
                </div>
                <p className="text-xs text-gray-500">Recommandé: 1920x1080px. Formats: PNG, JPG</p>
              </div>
            </div>
          )}
        </div>
        
        {/* Basic Settings */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Nom de l&apos;entreprise</label>
            <input type="text" className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500"
              value={config.branding.company_name}
              onChange={(e) => setConfig({...config, branding: {...config.branding, company_name: e.target.value}})} />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Police de caractères</label>
            <select className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500"
              value={config.branding.font_family}
              onChange={(e) => setConfig({...config, branding: {...config.branding, font_family: e.target.value}})}>
              <option value="Inter">Inter</option>
              <option value="Roboto">Roboto</option>
              <option value="Poppins">Poppins</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Couleur primaire</label>
            <input type="color" className="w-full h-12 border border-gray-300 rounded-lg"
              value={config.branding.primary_color}
              onChange={(e) => setConfig({...config, branding: {...config.branding, primary_color: e.target.value}})} />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Couleur secondaire</label>
            <input type="color" className="w-full h-12 border border-gray-300 rounded-lg"
              value={config.branding.secondary_color}
              onChange={(e) => setConfig({...config, branding: {...config.branding, secondary_color: e.target.value}})} />
          </div>
        </div>
        
        <div className="flex gap-4 pt-4">
          <button onClick={() => saveConfiguration('branding', config.branding)} disabled={saving}
            className="flex items-center gap-2 px-6 py-2 bg-pink-600 text-white rounded-lg hover:bg-pink-700 disabled:opacity-50">
            {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : <Save className="w-5 h-5" />}
            Enregistrer
          </button>
        </div>
      </div>
    );
  };

  const renderSecurityConfig = () => {
    if (!config?.security) return null;
    return (
      <div className="space-y-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
          <Shield className="w-5 h-5 text-red-600 mt-0.5" />
          <div>
            <h4 className="font-semibold text-red-900">Paramètres de Sécurité</h4>
            <p className="text-sm text-red-700 mt-1">Configurez les politiques de sécurité et d'authentification.</p>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Longueur min. mot de passe</label>
            <input type="number" className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500"
              value={config.security.password_min_length}
              onChange={(e) => setConfig({...config, security: {...config.security, password_min_length: parseInt(e.target.value)}})} />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Timeout session (min)</label>
            <input type="number" className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500"
              value={config.security.session_timeout_minutes}
              onChange={(e) => setConfig({...config, security: {...config.security, session_timeout_minutes: parseInt(e.target.value)}})} />
          </div>
        </div>
        <div className="space-y-3">
          {['password_require_uppercase', 'password_require_lowercase', 'password_require_numbers', 'password_require_special'].map((key) => (
            <div key={key} className="flex items-center gap-3">
              <input type="checkbox" className="w-5 h-5 text-red-600"
                checked={config.security[key]}
                onChange={(e) => setConfig({...config, security: {...config.security, [key]: e.target.checked}})} />
              <span className="text-sm text-gray-700">{key.replace('password_require_', 'Exiger ').replace('uppercase', 'majuscules').replace('lowercase', 'minuscules').replace('numbers', 'chiffres').replace('special', 'caractères spéciaux')}</span>
            </div>
          ))}
        </div>
        <div className="flex gap-4 pt-4">
          <button onClick={() => saveConfiguration('security', config.security)} disabled={saving}
            className="flex items-center gap-2 px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50">
            {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : <Save className="w-5 h-5" />}
            Enregistrer
          </button>
        </div>
      </div>
    );
  };

  const renderMapsConfig = () => {
    if (!config?.maps) return null;
    return (
      <div className="space-y-6">
        <div className="bg-teal-50 border border-teal-200 rounded-lg p-4 flex items-start gap-3">
          <Map className="w-5 h-5 text-teal-600 mt-0.5" />
          <div>
            <h4 className="font-semibold text-teal-900">Configuration Cartes & Géolocalisation</h4>
            <p className="text-sm text-teal-700 mt-1">Paramétrez les cartes et le suivi géographique des opérations.</p>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Centre carte - Latitude</label>
            <input type="number" step="0.0001" className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500"
              value={config.maps.default_map_center_lat}
              onChange={(e) => setConfig({...config, maps: {...config.maps, default_map_center_lat: parseFloat(e.target.value)}})} />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Centre carte - Longitude</label>
            <input type="number" step="0.0001" className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500"
              value={config.maps.default_map_center_lng}
              onChange={(e) => setConfig({...config, maps: {...config.maps, default_map_center_lng: parseFloat(e.target.value)}})} />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Zoom par défaut</label>
            <input type="number" min="1" max="20" className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500"
              value={config.maps.default_map_zoom}
              onChange={(e) => setConfig({...config, maps: {...config.maps, default_map_zoom: parseInt(e.target.value)}})} />
          </div>
        </div>
        <div className="space-y-3">
          <div className="flex items-center justify-between p-4 bg-white border rounded-lg">
            <div>
              <h4 className="font-medium text-gray-900">Optimisation des itinéraires</h4>
              <p className="text-sm text-gray-500">Calculer automatiquement le meilleur itinéraire</p>
            </div>
            <input type="checkbox" className="w-5 h-5 text-teal-600"
              checked={config.maps.enable_route_optimization}
              onChange={(e) => setConfig({...config, maps: {...config.maps, enable_route_optimization: e.target.checked}})} />
          </div>
          <div className="flex items-center justify-between p-4 bg-white border rounded-lg">
            <div>
              <h4 className="font-medium text-gray-900">Suivi en temps réel</h4>
              <p className="text-sm text-gray-500">Activer le tracking GPS des véhicules</p>
            </div>
            <input type="checkbox" className="w-5 h-5 text-teal-600"
              checked={config.maps.enable_real_time_tracking}
              onChange={(e) => setConfig({...config, maps: {...config.maps, enable_real_time_tracking: e.target.checked}})} />
          </div>
        </div>
        <div className="flex gap-4 pt-4">
          <button onClick={() => saveConfiguration('maps', config.maps)} disabled={saving}
            className="flex items-center gap-2 px-6 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:opacity-50">
            {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : <Save className="w-5 h-5" />}
            Enregistrer
          </button>
        </div>
      </div>
    );
  };

  const renderCurrencyConfig = () => {
    if (!config?.currency) return null;
    return (
      <div className="space-y-6">
        <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 flex items-start gap-3">
          <DollarSign className="w-5 h-5 text-emerald-600 mt-0.5" />
          <div>
            <h4 className="font-semibold text-emerald-900">Configuration Devises & Paiements</h4>
            <p className="text-sm text-emerald-700 mt-1">Gérez les devises, taxes et options de paiement.</p>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Devise par défaut</label>
            <select className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
              value={config.currency.default_currency}
              onChange={(e) => setConfig({...config, currency: {...config.currency, default_currency: e.target.value}})}>
              <option value="XOF">XOF (Franc CFA)</option>
              <option value="EUR">EUR (Euro)</option>
              <option value="USD">USD (Dollar)</option>
              <option value="GBP">GBP (Livre Sterling)</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Taux de TVA (%)</label>
            <input type="number" step="0.1" className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
              value={config.currency.tax_rate_percent}
              onChange={(e) => setConfig({...config, currency: {...config.currency, tax_rate_percent: parseFloat(e.target.value)}})} />
          </div>
        </div>
        <div className="space-y-3">
          <div className="flex items-center justify-between p-4 bg-white border rounded-lg">
            <div>
              <h4 className="font-medium text-gray-900">Paiement en ligne</h4>
              <p className="text-sm text-gray-500">Activer les paiements par carte/mobile money</p>
            </div>
            <input type="checkbox" className="w-5 h-5 text-emerald-600"
              checked={config.currency.enable_online_payment}
              onChange={(e) => setConfig({...config, currency: {...config.currency, enable_online_payment: e.target.checked}})} />
          </div>
          {config.currency.enable_online_payment && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Fournisseur de paiement</label>
              <select className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
                value={config.currency.payment_provider || ''}
                onChange={(e) => setConfig({...config, currency: {...config.currency, payment_provider: e.target.value}})}>
                <option value="">Sélectionner...</option>
                <option value="stripe">Stripe</option>
                <option value="paypal">PayPal</option>
                <option value="cinetpay">CinetPay</option>
                <option value="orange_money">Orange Money</option>
                <option value="mtn_mobile_money">MTN Mobile Money</option>
              </select>
            </div>
          )}
        </div>
        <div className="flex gap-4 pt-4">
          <button onClick={() => saveConfiguration('currency', config.currency)} disabled={saving}
            className="flex items-center gap-2 px-6 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50">
            {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : <Save className="w-5 h-5" />}
            Enregistrer
          </button>
        </div>
      </div>
    );
  };

  const renderWorkflowConfig = () => {
    if (!config?.workflow) return null;
    return (
      <div className="space-y-6">
        <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4 flex items-start gap-3">
          <GitBranch className="w-5 h-5 text-indigo-600 mt-0.5" />
          <div>
            <h4 className="font-semibold text-indigo-900">Configuration Workflow & Approbations</h4>
            <p className="text-sm text-indigo-700 mt-1">Définissez les règles d'approbation et d'automatisation.</p>
          </div>
        </div>
        <div className="space-y-4">
          <div className="flex items-center justify-between p-4 bg-white border rounded-lg">
            <div>
              <h4 className="font-medium text-gray-900">Approbation des devis</h4>
              <p className="text-sm text-gray-500">Exiger une validation manuelle des devis</p>
            </div>
            <input type="checkbox" className="w-5 h-5 text-indigo-600"
              checked={config.workflow.require_quote_approval}
              onChange={(e) => setConfig({...config, workflow: {...config.workflow, require_quote_approval: e.target.checked}})} />
          </div>
          {config.workflow.require_quote_approval && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Seuil d'approbation (XOF)</label>
              <input type="number" className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                placeholder="Ex: 500000"
                value={config.workflow.quote_approval_threshold || ''}
                onChange={(e) => setConfig({...config, workflow: {...config.workflow, quote_approval_threshold: parseFloat(e.target.value)}})} />
              <p className="text-xs text-gray-500 mt-1">Devis au-dessus de ce montant nécessitent une approbation</p>
            </div>
          )}
          <div className="flex items-center justify-between p-4 bg-white border rounded-lg">
            <div>
              <h4 className="font-medium text-gray-900">Attribution automatique</h4>
              <p className="text-sm text-gray-500">Assigner automatiquement les opérations aux opérateurs</p>
            </div>
            <input type="checkbox" className="w-5 h-5 text-indigo-600"
              checked={config.workflow.auto_assign_operations}
              onChange={(e) => setConfig({...config, workflow: {...config.workflow, auto_assign_operations: e.target.checked}})} />
          </div>
          <div className="flex items-center justify-between p-4 bg-white border rounded-lg">
            <div>
              <h4 className="font-medium text-gray-900">Mise à jour auto. des statuts</h4>
              <p className="text-sm text-gray-500">Changer automatiquement le statut selon les actions</p>
            </div>
            <input type="checkbox" className="w-5 h-5 text-indigo-600"
              checked={config.workflow.operation_status_auto_update}
              onChange={(e) => setConfig({...config, workflow: {...config.workflow, operation_status_auto_update: e.target.checked}})} />
          </div>
        </div>
        <div className="flex gap-4 pt-4">
          <button onClick={() => saveConfiguration('workflow', config.workflow)} disabled={saving}
            className="flex items-center gap-2 px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50">
            {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : <Save className="w-5 h-5" />}
            Enregistrer
          </button>
        </div>
      </div>
    );
  };

  const renderDocumentsConfig = () => {
    if (!config?.documents) return null;
    return (
      <div className="space-y-6">
        <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 flex items-start gap-3">
          <FileText className="w-5 h-5 text-orange-600 mt-0.5" />
          <div>
            <h4 className="font-semibold text-orange-900">Configuration Documents & Modèles</h4>
            <p className="text-sm text-orange-700 mt-1">Personnalisez les documents générés automatiquement.</p>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Modèle de facture</label>
            <select className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
              value={config.documents.default_invoice_template}
              onChange={(e) => setConfig({...config, documents: {...config.documents, default_invoice_template: e.target.value}})}>
              <option value="default">Modèle Standard</option>
              <option value="minimal">Modèle Minimal</option>
              <option value="detailed">Modèle Détaillé</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Modèle de devis</label>
            <select className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
              value={config.documents.default_quote_template}
              onChange={(e) => setConfig({...config, documents: {...config.documents, default_quote_template: e.target.value}})}>
              <option value="default">Modèle Standard</option>
              <option value="minimal">Modèle Minimal</option>
              <option value="detailed">Modèle Détaillé</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Préfixe numéro facture</label>
            <input type="text" className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
              value={config.documents.invoice_number_prefix}
              onChange={(e) => setConfig({...config, documents: {...config.documents, invoice_number_prefix: e.target.value}})} />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Préfixe numéro devis</label>
            <input type="text" className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
              value={config.documents.quote_number_prefix}
              onChange={(e) => setConfig({...config, documents: {...config.documents, quote_number_prefix: e.target.value}})} />
          </div>
        </div>
        <div className="flex items-center justify-between p-4 bg-white border rounded-lg">
          <div>
            <h4 className="font-medium text-gray-900">Génération automatique de facture</h4>
            <p className="text-sm text-gray-500">Créer automatiquement une facture après livraison</p>
          </div>
          <input type="checkbox" className="w-5 h-5 text-orange-600"
            checked={config.documents.auto_generate_invoice}
            onChange={(e) => setConfig({...config, documents: {...config.documents, auto_generate_invoice: e.target.checked}})} />
        </div>
        <div className="flex gap-4 pt-4">
          <button onClick={() => saveConfiguration('documents', config.documents)} disabled={saving}
            className="flex items-center gap-2 px-6 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:opacity-50">
            {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : <Save className="w-5 h-5" />}
            Enregistrer
          </button>
        </div>
      </div>
    );
  };

  const renderPricingConfig = () => {
    if (!config?.pricing) return null;
    return (
      <div className="space-y-6">
        <div className="bg-cyan-50 border border-cyan-200 rounded-lg p-4 flex items-start gap-3">
          <Tag className="w-5 h-5 text-cyan-600 mt-0.5" />
          <div>
            <h4 className="font-semibold text-cyan-900">Configuration Tarification & Commerciaux</h4>
            <p className="text-sm text-cyan-700 mt-1">Définissez les tarifs de base et stratégies commerciales.</p>
          </div>
        </div>
        <div className="space-y-4">
          <div className="flex items-center justify-between p-4 bg-white border rounded-lg">
            <div>
              <h4 className="font-medium text-gray-900">Tarification dynamique</h4>
              <p className="text-sm text-gray-500">Ajuster automatiquement les tarifs selon la demande</p>
            </div>
            <input type="checkbox" className="w-5 h-5 text-cyan-600"
              checked={config.pricing.enable_dynamic_pricing}
              onChange={(e) => setConfig({...config, pricing: {...config.pricing, enable_dynamic_pricing: e.target.checked}})} />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Tarif de base par km (XOF)</label>
              <input type="number" className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500"
                value={config.pricing.base_rate_per_km}
                onChange={(e) => setConfig({...config, pricing: {...config.pricing, base_rate_per_km: parseFloat(e.target.value)}})} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Tarif de base par tonne (XOF)</label>
              <input type="number" className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500"
                value={config.pricing.base_rate_per_ton}
                onChange={(e) => setConfig({...config, pricing: {...config.pricing, base_rate_per_ton: parseFloat(e.target.value)}})} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Supplément carburant (%)</label>
              <input type="number" step="0.1" className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500"
                value={config.pricing.fuel_surcharge_percent}
                onChange={(e) => setConfig({...config, pricing: {...config.pricing, fuel_surcharge_percent: parseFloat(e.target.value)}})} />
            </div>
          </div>
          <div className="flex items-center justify-between p-4 bg-white border rounded-lg">
            <div>
              <h4 className="font-medium text-gray-900">Remises sur volume</h4>
              <p className="text-sm text-gray-500">Appliquer des réductions pour les gros clients</p>
            </div>
            <input type="checkbox" className="w-5 h-5 text-cyan-600"
              checked={config.pricing.enable_volume_discounts}
              onChange={(e) => setConfig({...config, pricing: {...config.pricing, enable_volume_discounts: e.target.checked}})} />
          </div>

          {/* Insurance Configuration */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-6">
            <div className="flex items-start gap-3 mb-4">
              <Shield className="w-5 h-5 text-blue-600 mt-0.5" />
              <div>
                <h4 className="font-semibold text-blue-900">Taux d'Assurance</h4>
                <p className="text-sm text-blue-700 mt-1">Configuration des taux d'assurance selon la valeur des marchandises</p>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Taux valeur basse (%)</label>
                <input type="number" step="0.01" className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  value={config.pricing?.insurance_rate_low_value || 2}
                  onChange={(e) => setConfig({...config, pricing: {...config.pricing, insurance_rate_low_value: parseFloat(e.target.value)}})}
                  placeholder="2.0" />
                <p className="text-xs text-gray-500 mt-1">Pour marchandises standard</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Taux valeur élevée (%)</label>
                <input type="number" step="0.01" className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  value={config.pricing?.insurance_rate_high_value || 3}
                  onChange={(e) => setConfig({...config, pricing: {...config.pricing, insurance_rate_high_value: parseFloat(e.target.value)}})}
                  placeholder="3.0" />
                <p className="text-xs text-gray-500 mt-1">Pour haute valeur</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Seuil valeur élevée (FCFA)</label>
                <input type="number" step="100000" className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  value={config.pricing?.high_value_threshold || 5000000}
                  onChange={(e) => setConfig({...config, pricing: {...config.pricing, high_value_threshold: parseFloat(e.target.value)}})}
                  placeholder="5000000" />
                <p className="text-xs text-gray-500 mt-1">Seuil de déclenchement</p>
              </div>
            </div>
          </div>
        </div>
        <div className="flex gap-4 pt-4">
          <button onClick={() => saveConfiguration('pricing', config.pricing)} disabled={saving}
            className="flex items-center gap-2 px-6 py-2 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700 disabled:opacity-50">
            {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : <Save className="w-5 h-5" />}
            Enregistrer
          </button>
        </div>
      </div>
    );
  };

  const renderMaintenanceConfig = () => {
    if (!config?.maintenance) return null;
    return (
      <div className="space-y-6">
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 flex items-start gap-3">
          <Settings className="w-5 h-5 text-gray-600 mt-0.5" />
          <div>
            <h4 className="font-semibold text-gray-900">Configuration Système & Maintenance</h4>
            <p className="text-sm text-gray-700 mt-1">Gérez la maintenance, sauvegardes et paramètres système.</p>
          </div>
        </div>
        <div className="space-y-4">
          <div className="border-l-4 border-yellow-500 bg-yellow-50 p-4 rounded">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-yellow-600 mt-0.5" />
              <div>
                <h4 className="font-medium text-yellow-900">Mode Maintenance</h4>
                <p className="text-sm text-yellow-700 mt-1">
                  Activer ce mode rendra la plateforme inaccessible aux utilisateurs
                </p>
                <div className="flex items-center gap-3 mt-3">
                  <input type="checkbox" className="w-5 h-5 text-yellow-600"
                    checked={config.maintenance.maintenance_mode}
                    onChange={(e) => setConfig({...config, maintenance: {...config.maintenance, maintenance_mode: e.target.checked}})} />
                  <span className="font-medium text-yellow-900">
                    {config.maintenance.maintenance_mode ? '🔴 Mode actif' : '🟢 Mode désactivé'}
                  </span>
                </div>
              </div>
            </div>
          </div>
          {config.maintenance.maintenance_mode && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Message de maintenance</label>
              <textarea className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500" rows="3"
                placeholder="Nous effectuons une maintenance..."
                value={config.maintenance.maintenance_message}
                onChange={(e) => setConfig({...config, maintenance: {...config.maintenance, maintenance_message: e.target.value}})} />
            </div>
          )}
          <div className="flex items-center justify-between p-4 bg-white border rounded-lg">
            <div>
              <h4 className="font-medium text-gray-900">Sauvegardes automatiques</h4>
              <p className="text-sm text-gray-500">Effectuer des sauvegardes régulières de la base de données</p>
            </div>
            <input type="checkbox" className="w-5 h-5 text-gray-600"
              checked={config.maintenance.auto_backup_enabled}
              onChange={(e) => setConfig({...config, maintenance: {...config.maintenance, auto_backup_enabled: e.target.checked}})} />
          </div>
          {config.maintenance.auto_backup_enabled && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Fréquence de sauvegarde (heures)</label>
              <input type="number" className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500"
                value={config.maintenance.backup_frequency_hours}
                onChange={(e) => setConfig({...config, maintenance: {...config.maintenance, backup_frequency_hours: parseInt(e.target.value)}})} />
            </div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Rétention des données (jours)</label>
              <input type="number" className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500"
                value={config.maintenance.data_retention_days}
                onChange={(e) => setConfig({...config, maintenance: {...config.maintenance, data_retention_days: parseInt(e.target.value)}})} />
            </div>
          </div>
          <div className="flex items-center justify-between p-4 bg-white border rounded-lg">
            <div>
              <h4 className="font-medium text-gray-900">Logs d'audit</h4>
              <p className="text-sm text-gray-500">Enregistrer toutes les actions des utilisateurs</p>
            </div>
            <input type="checkbox" className="w-5 h-5 text-gray-600"
              checked={config.maintenance.enable_audit_logs}
              onChange={(e) => setConfig({...config, maintenance: {...config.maintenance, enable_audit_logs: e.target.checked}})} />
          </div>
        </div>
        <div className="flex gap-4 pt-4">
          <button onClick={() => saveConfiguration('maintenance', config.maintenance)} disabled={saving}
            className="flex items-center gap-2 px-6 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-800 disabled:opacity-50">
            {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : <Save className="w-5 h-5" />}
            Enregistrer
          </button>
        </div>
      </div>
    );
  };

  const renderPlaceholder = (tabId) => {
    const tabInfo = tabs.find(t => t.id === tabId);
    return (
      <div className="text-center py-12">
        <div className="inline-flex items-center justify-center w-16 h-16 bg-gray-100 rounded-full mb-4">
          {tabInfo && <tabInfo.icon className={`w-8 h-8 ${tabInfo.color}`} />}
        </div>
        <h3 className="text-lg font-semibold text-gray-900 mb-2">
          {tabInfo?.label}
        </h3>
        <p className="text-gray-500">
          Configuration fonctionnelle. UI détaillée à venir.
        </p>
      </div>
    );
  };

  const renderContent = () => {
    if (loading) {
      return (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 text-gray-400 animate-spin" />
        </div>
      );
    }

    if (!config) {
      return (
        <div className="text-center py-12 text-gray-500">
          Erreur de chargement de la configuration
        </div>
      );
    }

    switch (activeTab) {
      case 'webhooks':
        return <WebhooksIntegrations />;
      case 'registration':
        return <RegistrationConfigPanel />;
      case 'truck-database':
        return <TruckDatabaseConfigPanel user={user} />;
      case 'truck-monitoring':
        return <TruckDBMonitoringDashboard user={user} />;
      case 'email':
        return renderEmailConfig();
      case 'whatsapp':
        return <WhatsAppConfigPanel config={config} setConfig={setConfig} onSave={saveConfiguration} saving={saving} />;
      case 'openwa':
        return <OpenWaConfigPanel />;
      case 'notifications':
        return renderNotificationsConfig();
      case 'notification-templates':
        return <NotificationTemplates />;
      case 'negotiation':
        return <NegotiationConfigPanel />;
      case 'api':
        return renderAPIConfig();
      case 'ai':
        return <AiConfigPanel />;
      case 'storage':
        return renderStorageConfig();
      case 'branding':
        return renderBrandingConfig();
      case 'security':
        return renderSecurityConfig();
      case 'maps':
        return renderMapsConfig();
      case 'currency':
        return renderCurrencyConfig();
      case 'workflow':
        return renderWorkflowConfig();
      case 'documents':
        return renderDocumentsConfig();
      case 'pricing':
        return renderPricingConfig();
      case 'maintenance':
        return renderMaintenanceConfig();
      default:
        return renderPlaceholder(activeTab);
    }
  };

  return (
    <div className="max-w-7xl mx-auto">
      <div className="mb-6">
        <h1 className="text-xl md:text-3xl font-bold text-gray-900">Configuration Système</h1>
        <p className="text-gray-500 mt-2">
          Gérez tous les paramètres de la plateforme depuis un seul endroit
        </p>
      </div>

      <div className="grid grid-cols-12 gap-6">
        {/* Sidebar Navigation */}
        <div className="col-span-3">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
            <nav className="space-y-1 p-2">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                const isActive = activeTab === tab.id;
                
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`
                      w-full flex items-center gap-3 px-4 py-3 rounded-lg text-left transition-all
                      ${isActive 
                        ? 'bg-gray-900 text-white shadow-md' 
                        : 'text-gray-700 hover:bg-gray-50'
                      }
                    `}
                  >
                    <Icon className={`w-5 h-5 ${isActive ? 'text-white' : tab.color}`} />
                    <span className="text-sm font-medium">{tab.label}</span>
                  </button>
                );
              })}
            </nav>
          </div>
        </div>

        {/* Main Content */}
        <div className="col-span-9">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            {renderContent()}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SystemConfiguration;
